import React, { useState, useEffect, useRef } from 'react';

export default function WaitlistModal({ isOpen, onClose }) {
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({ fname: '', lname: '', email: '', phone: '', type: '' });
  const fnameRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => fnameRef.current?.focus(), 350);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && isOpen) handleClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setShowSuccess(false);
      setForm({ fname: '', lname: '', email: '', phone: '', type: '' });
      setErrors({});
    }, 300);
  };

  const validate = () => {
    const newErrors = {};
    if (!form.fname.trim()) newErrors.fname = true;
    if (!form.lname.trim()) newErrors.lname = true;
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = true;
    if (!form.type) newErrors.type = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.fname,
          lastName:  form.lname,
          email:     form.email,
          phone:     form.phone,
          type:      form.type,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Something went wrong');
      }

      setShowSuccess(true);
    } catch (err) {
      console.error(err);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    setErrors(prev => ({ ...prev, [field]: false }));
  };

  if (!isOpen) return null;

  return (
    <div
      className={`wl-backdrop ${isOpen ? 'wl-open' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="wlTitle"
      onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="wl-modal">
        <button className="wl-close" onClick={handleClose} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="wl-accent" aria-hidden="true">
          <div className="wl-accent-ring wl-ring-1"></div>
          <div className="wl-accent-ring wl-ring-2"></div>
          <div className="wl-accent-orb"></div>
        </div>

        <div className="wl-header">
          <div className="wl-badge"><span className="wl-badge-dot"></span>Early Access</div>
          <h2 className="wl-title" id="wlTitle">Join the Waitlist</h2>
          <p className="wl-subtitle">Be among the first to experience Afghanistan's new era of digital payments.</p>
        </div>

        {!showSuccess ? (
          <div className="wl-form-wrap">
            <div className="wl-field-row">
              <div className="wl-field">
                <label className="wl-label" htmlFor="wl-fname">First Name</label>
                <input ref={fnameRef} className={`wl-input${errors.fname ? ' wl-error' : ''}`} type="text" id="wl-fname"
                  placeholder="Ahmad" autoComplete="given-name" value={form.fname} onChange={handleChange('fname')} />
              </div>
              <div className="wl-field">
                <label className="wl-label" htmlFor="wl-lname">Last Name</label>
                <input className={`wl-input${errors.lname ? ' wl-error' : ''}`} type="text" id="wl-lname"
                  placeholder="Karimi" autoComplete="family-name" value={form.lname} onChange={handleChange('lname')} />
              </div>
            </div>

            <div className="wl-field">
              <label className="wl-label" htmlFor="wl-email">Email Address</label>
              <div className="wl-input-icon-wrap">
                <svg className="wl-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <input className={`wl-input wl-input--icon${errors.email ? ' wl-error' : ''}`} type="email" id="wl-email"
                  placeholder="you@example.com" autoComplete="email" value={form.email} onChange={handleChange('email')} />
              </div>
            </div>

            <div className="wl-field">
              <label className="wl-label" htmlFor="wl-phone">Phone Number <span className="wl-optional">(optional)</span></label>
              <div className="wl-input-icon-wrap">
                <svg className="wl-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12 19.79 19.79 0 0 1 1.08 3.4 2 2 0 0 1 3.06 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <input className="wl-input wl-input--icon" type="tel" id="wl-phone"
                  placeholder="+93 70 000 0000" autoComplete="tel" value={form.phone} onChange={handleChange('phone')} />
              </div>
            </div>

            <div className="wl-field">
              <label className="wl-label" htmlFor="wl-type">I am joining as</label>
              <div className="wl-select-wrap">
                <select className={`wl-select${errors.type ? ' wl-error' : ''}`} id="wl-type" value={form.type} onChange={handleChange('type')}>
                  <option value="" disabled>Select your role</option>
                  <option value="individual">Individual / Personal</option>
                  <option value="business">Business / SME</option>
                  <option value="developer">Developer / Partner</option>
                  <option value="government">Government / NGO</option>
                </select>
                <svg className="wl-select-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>

            <p className="wl-privacy">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Your information is safe. We'll only use it to notify you when Chattak launches.
            </p>

            <a className={`m-button__wrap${loading ? ' wl-loading' : ''}`} onClick={handleSubmit} data-btn="up" href="#!">
              <span className="m-b-bg" aria-hidden="true"></span>
              <span className="m-b-text" aria-hidden="true">
                <span className="m-b-text-static wl-submit-text">{loading ? 'Saving...' : 'Reserve my spot'}</span>
                <span className="m-b-text-up wl-submit-text">{loading ? 'Saving...' : 'Reserve my spot'}</span>
              </span>
              <span className="m-b-arrow" aria-hidden="true">
                <span className="m-b-arrow-static">
                  <img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} />
                </span>
                <span className="m-b-arrow-up">
                  <img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} />
                </span>
              </span>
              <span className="sr-only">Reserve my spot</span>
            </a>
          </div>
        ) : (
          <div className="wl-success">
            <div className="wl-success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3 className="wl-success-title">You're on the list!</h3>
            <p className="wl-success-msg">Thanks for signing up. We'll reach out as soon as Chattak is ready for you.</p>
            <button className="wl-close-btn" onClick={handleClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
